import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Target, Trophy, FileText, Download, Calendar, Users, Flag, Info } from 'lucide-react';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
const t = (k, o) => i18n.t(k, o);

const getplanData = () => ({
  'strategiplan': {
    title: t('pg.StrategiSportsplanDetail.ae8017'),
    subtitle: t('pg.StrategiSportsplanDetail.6f0ec6'),
    period: '2026 - 2031',
    icon: Target,
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
    intro: t('pg.StrategiSportsplanDetail.ed735b'),
    sections: [
      t('pg.StrategiSportsplanDetail.2f040a'),
      t('pg.StrategiSportsplanDetail.b2d864'),
      t('pg.StrategiSportsplanDetail.e5e6db'),
      t('pg.StrategiSportsplanDetail.3855ce'),
      t('pg.StrategiSportsplanDetail.1df3cc'),
      t('pg.StrategiSportsplanDetail.e6301e'),
    ],
    documents: [],
  },
  'sportsplan': {
    title: t('pg.StrategiSportsplanDetail.5a18c9'),
    subtitle: t('pg.StrategiSportsplanDetail.2f7f74'),
    period: '2026 - 2031',
    icon: Trophy,
    iconBg: 'bg-green-500/10',
    iconColor: 'text-green-500',
    intro: t('pg.StrategiSportsplanDetail.628116'),
    sections: [
      t('pg.StrategiSportsplanDetail.c8f75c'),
      t('pg.StrategiSportsplanDetail.4ebfcc'),
      t('pg.StrategiSportsplanDetail.575fab'),
      t('pg.StrategiSportsplanDetail.57eff7'),
      t('pg.StrategiSportsplanDetail.b09fa3'),
      t('pg.StrategiSportsplanDetail.22a4f6'),
    ],
    subPages: [
      {
        title: t('pg.StrategiSportsplanDetail.9fc64e'),
        description: t('pg.StrategiSportsplanDetail.3bed49'),
        path: '/strategi-sportsplan/sportsplan/fotball',
      },
    ],
    documents: [],
  },
  'sportsplan-fotball': {
    title: t('pg.StrategiSportsplanDetail.268cfb'),
    subtitle: t('pg.StrategiSportsplanDetail.a54bd5'),
    period: '2026 - 2031',
    icon: Trophy,
    iconBg: 'bg-green-500/10',
    iconColor: 'text-green-400',
    intro: t('pg.StrategiSportsplanDetail.cb9dd8'),
    parentPath: '/strategi-sportsplan/sportsplan',
    parentTitle: t('pg.StrategiSportsplanDetail.ae979d'),
    sections: [
      t('pg.StrategiSportsplanDetail.cf2dcc'),
      t('pg.StrategiSportsplanDetail.29223c'),
      t('pg.StrategiSportsplanDetail.b4829c'),
      t('pg.StrategiSportsplanDetail.3b05b8'),
      t('pg.StrategiSportsplanDetail.b7b528'),
      t('pg.StrategiSportsplanDetail.c08386'),
      t('pg.StrategiSportsplanDetail.41babc'),
    ],
    documents: [],
  },
});

export const StrategiSportsplanDetail = () => {
  useTranslation();
  const { type, subtype } = useParams();
  
  let dataKey = type;
  if (subtype) {
    dataKey = `${type}-${subtype}`;
  }
  
  const data = getplanData()[dataKey];

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="plan-not-found">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{t('pg.StrategiSportsplanDetail.75b48f')}</h1>
          <Link to="/strategi-sportsplan">
            <button className="bg-green-500 text-white font-bold px-6 py-3 rounded-lg">
              {t('pg.StrategiSportsplanDetail.9470c2')}
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = data.icon;
  const backPath = data.parentPath || '/strategi-sportsplan';
  const backTitle = data.parentTitle || 'Strategi & Sportsplan';

  return (
    <div data-testid={`plan-${dataKey}-page`} className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to={backPath}
            className="inline-flex items-center gap-2 text-slate-500 hover:text-green-500 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('pg.StrategiSportsplanDetail.041468')} {backTitle}
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-14 h-14 ${data.iconBg} rounded-xl flex items-center justify-center`}>
              <IconComponent className={`w-7 h-7 ${data.iconColor}`} />
            </div>
            <div>
              <p className="text-green-500 font-medium uppercase tracking-widest mb-1">{data.subtitle}</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900">
                {data.title}
              </h1>
            </div>
          </div>
          
          <p className="text-slate-600 text-lg">
            {data.intro}
          </p>
        </div>
      </section>

      {/* Info Banner */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5 flex items-start gap-4">
            <Info className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-slate-600">
              {t('pg.StrategiSportsplanDetail.d035f9')}
            </p>
          </div>
        </div>
      </section>

      {/* Period */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl border border-white/10 p-6 inline-flex items-center gap-4">
            <Calendar className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider">{t('pg.StrategiSportsplanDetail.630e6b')}</p>
              <p className="text-slate-900 font-bold text-lg">{data.period}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      {data.sections && data.sections.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border border-white/10 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-5 h-5 text-green-500" />
                {t('pg.StrategiSportsplanDetail.a4f838')}
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {data.sections.map((section, index) => (
                  <div key={index} className="flex items-center gap-3 bg-slate-100/50 rounded-lg p-4">
                    <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-green-500 font-bold text-sm">{index + 1}</span>
                    </div>
                    <span className="text-slate-600">{section}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Sub-pages */}
      {data.subPages && data.subPages.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border border-green-500/30 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Users className="w-5 h-5 text-green-500" />
                {t('pg.StrategiSportsplanDetail.11e650')}
              </h2>
              <div className="space-y-3">
                {data.subPages.map((subPage, index) => (
                  <Link
                    key={index}
                    to={subPage.path}
                    className="flex items-center gap-4 bg-slate-100/50 hover:bg-slate-100 rounded-xl p-4 transition-colors group"
                  >
                    <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Flag className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-900 font-medium group-hover:text-green-500 transition-colors">
                        {subPage.title}
                      </p>
                      <p className="text-slate-400 text-sm">{subPage.description}</p>
                    </div>
                    <ArrowLeft className="w-5 h-5 text-slate-400 group-hover:text-green-500 rotate-180 transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Documents */}
      {data.documents && data.documents.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border border-white/10 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Download className="w-5 h-5 text-green-500" />
                {t('pg.StrategiSportsplanDetail.aabad0')}
              </h2>
              <div className="space-y-3">
                {data.documents.map((doc, index) => (
                  <a
                    key={index}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 bg-slate-100/50 hover:bg-slate-100 rounded-xl p-4 transition-colors group"
                  >
                    <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-red-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-900 font-medium group-hover:text-green-500 transition-colors">
                        {doc.name}
                      </p>
                      <p className="text-slate-400 text-sm">{doc.description}</p>
                    </div>
                    <Download className="w-5 h-5 text-slate-400 group-hover:text-green-500 transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Placeholder for documents */}
      {(!data.documents || data.documents.length === 0) && (
        <section className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/50 rounded-2xl border border-dashed border-white/10 p-8 text-center">
              <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-slate-400">
                {t('pg.StrategiSportsplanDetail.935ddf')}
              </p>
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 text-green-500 font-medium mt-4 hover:underline"
              >
                {t('pg.StrategiSportsplanDetail.c3a7fb')}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default StrategiSportsplanDetail;
